import { useState } from "react";
import { Lock, User, Cpu, Phone, MessageSquare, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerLogin = ({ onLogin }) => {
  // Arriving via the home page's "Create Account" button (/portal?signup=1)
  // opens the signup form directly instead of the login form.
  const [mode, setMode] = useState(() =>
    new URLSearchParams(window.location.search).get("signup") ? "signup" : "login"
  ); // login | signup | done
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Signup fields
  const [suName, setSuName] = useState("");
  const [suPhone, setSuPhone] = useState("");
  const [suUsername, setSuUsername] = useState("");
  const [suPassword, setSuPassword] = useState("");
  const [suPassword2, setSuPassword2] = useState("");
  const [suNote, setSuNote] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(
        `${API}/portal/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        }
      );

      if (!res.ok) {
        throw new Error("Invalid username or password");
      }

      const data = await res.json();
      localStorage.setItem("customer_session", JSON.stringify(data));
      onLogin(data);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    if (suPassword !== suPassword2) {
      setError("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/portal/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: suName,
          phone: suPhone,
          username: suUsername,
          password: suPassword,
          note: suNote,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Signup failed — please try again");
      }
      setMode("done");
    } catch (err) {
      setError(err.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
  };

  const inputClass = "pl-12 py-6 bg-[#0F0F0F] border-[#27272A] text-white text-lg";

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#00E054] to-[#00C2FF] flex items-center justify-center mx-auto mb-6 shadow-[0_0_60px_rgba(0,224,84,0.3)]">
            <Cpu className="text-black" size={48} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">WKBeast Farm</h1>
          <p className="text-gray-500">
            {mode === "signup" ? "Create your account" : "Customer Portal"}
          </p>
        </div>

        {/* Signup received confirmation */}
        {mode === "done" && (
          <div className="text-center space-y-4">
            <div className="p-6 bg-[#00E054]/10 border border-[#00E054]/30 rounded-xl">
              <CheckCircle className="text-[#00E054] mx-auto mb-3" size={40} />
              <h2 className="text-white text-lg font-bold mb-2">Request received!</h2>
              <p className="text-gray-400 text-sm">
                We'll review your request and activate your account shortly.
                You'll get a WhatsApp message as soon as it's ready.
              </p>
            </div>
            <button
              onClick={() => switchMode("login")}
              className="text-gray-500 text-sm hover:text-white transition-colors inline-flex items-center gap-1"
              data-testid="signup-back-to-login"
            >
              <ArrowLeft size={14} /> Back to login
            </button>
          </div>
        )}

        {/* Login Form */}
        {mode === "login" && (
          <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username"
                  className={inputClass}
                  required
                  data-testid="customer-username"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className={inputClass}
                  required
                  data-testid="customer-password"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 bg-[#00E054] text-black font-bold text-lg hover:bg-[#00E054]/90 transition-all hover:shadow-[0_0_30px_rgba(0,224,84,0.5)]"
                data-testid="customer-login-btn"
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </form>

            {/* Signup link */}
            <div className="text-center mt-6">
              <button
                onClick={() => switchMode("signup")}
                className="text-[#00E054] text-sm hover:underline"
                data-testid="show-signup-btn"
              >
                New customer? Create an account
              </button>
            </div>

            {/* Footer */}
            <p className="text-center text-gray-600 text-sm mt-6">
              Contact admin if you forgot your password
            </p>
          </>
        )}

        {/* Signup Form */}
        {mode === "signup" && (
          <>
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <Input
                  type="text"
                  value={suName}
                  onChange={(e) => setSuName(e.target.value)}
                  placeholder="Full name"
                  className={inputClass}
                  required
                  data-testid="signup-name"
                />
              </div>

              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <Input
                  type="tel"
                  value={suPhone}
                  onChange={(e) => setSuPhone(e.target.value)}
                  placeholder="WhatsApp number (e.g. +961 3 123456)"
                  className={inputClass}
                  required
                  data-testid="signup-phone"
                />
              </div>

              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <Input
                  type="text"
                  value={suUsername}
                  onChange={(e) => setSuUsername(e.target.value)}
                  placeholder="Choose a username"
                  className={inputClass}
                  required
                  data-testid="signup-username"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <Input
                  type="password"
                  value={suPassword}
                  onChange={(e) => setSuPassword(e.target.value)}
                  placeholder="Choose a password (min 6 characters)"
                  className={inputClass}
                  required
                  minLength={6}
                  data-testid="signup-password"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <Input
                  type="password"
                  value={suPassword2}
                  onChange={(e) => setSuPassword2(e.target.value)}
                  placeholder="Repeat password"
                  className={inputClass}
                  required
                  minLength={6}
                  data-testid="signup-password2"
                />
              </div>

              <div className="relative">
                <MessageSquare className="absolute left-4 top-4 text-gray-500" size={20} />
                <textarea
                  value={suNote}
                  onChange={(e) => setSuNote(e.target.value)}
                  placeholder="Optional: tell us about your machines (e.g. 2x L9 hosted since May)"
                  rows={2}
                  maxLength={500}
                  className="w-full pl-12 pr-4 py-3 bg-[#0F0F0F] border border-[#27272A] rounded-lg text-white text-sm focus:outline-none focus:border-[#00E054]/50 transition-colors resize-none"
                  data-testid="signup-note"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 bg-[#00E054] text-black font-bold text-lg hover:bg-[#00E054]/90 transition-all hover:shadow-[0_0_30px_rgba(0,224,84,0.5)]"
                data-testid="signup-submit-btn"
              >
                {loading ? "Sending..." : "Request Account"}
              </Button>
            </form>

            <p className="text-center text-gray-600 text-xs mt-4">
              Your account is activated after a quick review — you'll be notified on WhatsApp.
            </p>

            <div className="text-center mt-4">
              <button
                onClick={() => switchMode("login")}
                className="text-gray-500 text-sm hover:text-white transition-colors inline-flex items-center gap-1"
                data-testid="signup-cancel-btn"
              >
                <ArrowLeft size={14} /> Back to login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerLogin;
